import { createContext, useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { SourcesResponse } from "@/types/pocketbase-types";
import { type IArticle } from "@/lib/article-utils";
import { organizeArticlesByDate, sourceToArticle } from "@/lib/article-utils";
import { pb } from "@/lib/pocketbase";
import { Collections } from "@/types/pocketbase-types";

interface ArticlesContextType {
  articles: IArticle[];
  isLoading: boolean;
  error: any;
}

const ArticlesContext = createContext<ArticlesContextType | undefined>(
  undefined
);

export const ArticlesProvider = ({ children }: { children: ReactNode }) => {
  const {
    data: sources = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["articles"],
    queryFn: async (): Promise<SourcesResponse[]> => {
      const records = await pb.collection(Collections.Sources).getList(1, 90, {
        filter: 'status="processed"',
        sort: "-created",
        expand: "section",
      });
      return records.items as unknown as SourcesResponse[];
    },
    staleTime: 1000 * 60 * 5,
  });

  const articles = useMemo(() => sources.map(sourceToArticle), [sources]);

  return (
    <ArticlesContext.Provider value={{ articles, isLoading, error }}>
      {children}
    </ArticlesContext.Provider>
  );
};

// 🔑 useArticles now accepts an optional filter param
export const useArticles = (quizTaken?: number) => {
  const context = useContext(ArticlesContext);
  if (!context) {
    throw new Error("useArticles must be used within ArticlesProvider");
  }

  const { articles, isLoading, error } = context;

  const filteredArticles = useMemo(() => {
    if (quizTaken === undefined) return articles;
    return articles.filter((a) => a.quiz_taken === quizTaken);
  }, [articles, quizTaken]);

  const articlesByDate = useMemo(
    () => organizeArticlesByDate(filteredArticles),
    [filteredArticles]
  );

  return { articlesByDate, isLoading, error };
};
